/**
 * Welk bestand een kraam mag insturen voor zijn menukaart.
 *
 * Je weet nooit wat er komt. De een stuurt een nette PDF, de ander een foto met
 * een duim in beeld, en een derde een Word-bestand. Alles wat we kunnen lezen
 * accepteren we; bij de rest zeggen we in één zin wat de kraam moet doen — geen
 * foutmelding, maar een aanwijzing.
 *
 * Wat we kunnen lezen is bepaald door wat het model aankan: PDF, JPEG, PNG,
 * GIF en WebP. iPhone-foto's in HEIC-formaat niet; die zet een telefoon meestal
 * zelf om bij het uploaden, maar niet altijd.
 */

export const GEACCEPTEERDE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

/** Wat je in het bestandskiezer-venster meegeeft. */
export const BESTANDSKIEZER_ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,image/*,application/pdf';

/** Een foto van een moderne telefoon is 2 tot 5 MB; een PDF met foto's meer. */
export const MAXIMALE_GROOTTE_BYTES = 20 * 1024 * 1024;

export interface Bestandsoordeel {
  bruikbaar: boolean;
  /** In gewone taal. Leeg als er niets aan de hand is. */
  melding: string | null;
}

const GOED: Bestandsoordeel = { bruikbaar: true, melding: null };

function opExtensie(naam: string): string {
  const punt = naam.lastIndexOf('.');
  return punt === -1 ? '' : naam.slice(punt + 1).toLowerCase();
}

/**
 * Beoordeelt een bestand vóór het uploaden. Het type dat de browser meegeeft
 * is niet altijd gevuld — vooral niet bij bestanden uit een cloudmap — dus de
 * extensie telt ook mee.
 */
export function beoordeelMenubestand(bestand: {
  naam: string;
  type: string;
  grootte: number;
}): Bestandsoordeel {
  const extensie = opExtensie(bestand.naam);

  if (bestand.grootte === 0) {
    return { bruikbaar: false, melding: 'Dit bestand is leeg. Probeer het opnieuw.' };
  }
  if (bestand.grootte > MAXIMALE_GROOTTE_BYTES) {
    return {
      bruikbaar: false,
      melding:
        'Dit bestand is te groot. Maak een foto van de kaart in plaats van een scan, of stuur de bladzijden apart.',
    };
  }

  if ((GEACCEPTEERDE_TYPES as readonly string[]).includes(bestand.type)) return GOED;
  if (['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extensie)) return GOED;

  if (
    bestand.type === 'image/heic' ||
    bestand.type === 'image/heif' ||
    ['heic', 'heif'].includes(extensie)
  ) {
    return {
      bruikbaar: false,
      melding:
        'Dit is een iPhone-foto die wij niet kunnen openen. Maak er een schermafbeelding van en stuur die — dat werkt altijd.',
    };
  }

  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pages', 'numbers'].includes(extensie)) {
    return {
      bruikbaar: false,
      melding: 'Sla dit bestand op als PDF en stuur die. Of maak een foto van je kaart.',
    };
  }

  if (['heic', 'tif', 'tiff', 'bmp', 'svg'].includes(extensie)) {
    return {
      bruikbaar: false,
      melding: 'Dit soort afbeelding kunnen wij niet openen. Stuur een gewone foto of een PDF.',
    };
  }

  if (['zip', 'rar', '7z'].includes(extensie)) {
    return {
      bruikbaar: false,
      melding: 'Pak het bestand eerst uit en stuur de kaart zelf, als PDF of als foto.',
    };
  }

  return {
    bruikbaar: false,
    melding: 'Stuur je kaart als PDF of als foto. Een foto met je telefoon is prima.',
  };
}

/** Wat er onder de uploadknop staat, zodat niemand hoeft te gokken. */
export const UPLOAD_UITLEG =
  'Stuur je kaart als PDF of als foto. Een foto met je telefoon is prima, als de prijzen maar leesbaar zijn.';
